import React from 'react';
import Colors from '@/constants/colors';
import GridTile from './GridTile';

interface Place {
  id: string;
  imageUrl: string;
  titleEn: string;
  titleRu: string;
  titlePl: string;
}

type GridSize = '1x1' | '2x1' | '3x1' | '1x2' | '2x2' | '3x2';

interface MasonryGridProps {
  places: Place[];
  language: 'en' | 'ru' | 'pl';
  onPlaceClick?: (placeId: string) => void;
}

/**
 * Grid Gap Configuration
 *
 * This constant is the single source of truth for spacing between grid items.
 * If you change this value, you must also update the aspect-ratio calculations
 * for multi-row items (1x2, 2x2, 3x2) in GridTile.tsx:
 *
 * Formula for multi-row aspect-ratios:
 *   aspect-ratio = columns / (rows + (gap/cellHeight))
 *
 * Example with GRID_GAP = 4px:
 *   [1x2]: 1 / (2 + 4/110) = 1 / 2.036 ≈ 1 / 2.08
 *
 * If you change gap, recalculate and update GridTile.tsx aspectRatioMap
 */
export const GRID_GAP = 4; // pixels

const GRID_PATTERN: GridSize[] = [
  '3x1',
  '2x1', '1x1',
  '1x1', '1x1', '1x1',
  '2x1', '1x2',
  '2x1',
  '3x1',
  '3x2',
  '1x1', '2x2',
  '1x1',
  '1x1', '2x1',
  '1x1', '2x1',
  '2x1', '1x1',
];

export default function MasonryGrid({
  places,
  language,
  onPlaceClick,
}: MasonryGridProps) {
  if (!places || places.length === 0) {
    return null;
  }

  // Create items array: repeat pattern and cycle through places
  const items = GRID_PATTERN.map((size, index) => {
    const placeIndex = index % places.length;
    const place = places[placeIndex];
    const title = getTitle(place, language);

    return {
      id: `${place.id}-${index}`,
      place,
      size,
      title,
    };
  });

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        {items.map((item) => (
          <GridTile
            key={item.id}
            id={item.place.id}
            imageUrl={item.place.imageUrl}
            imageAlt={item.title}
            title={item.title}
            size={item.size}
            onCardClick={() => onPlaceClick?.(item.place.id)}
            showFavoriteButton={false}
          />
        ))}
      </div>
    </div>
  );
}

function getTitle(place: Place, language: 'en' | 'ru' | 'pl'): string {
  switch (language) {
    case 'ru':
      return place.titleRu;
    case 'pl':
      return place.titlePl;
    case 'en':
    default:
      return place.titleEn;
  }
}

const styles = {
  container: {
    width: '100%',
    padding: '24px',
    backgroundColor: Colors.background,
  } as React.CSSProperties,

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: `${GRID_GAP}px`,
    gridAutoRows: 'auto',
  } as React.CSSProperties,
};
